/* eslint eslint-comments/no-use: off */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IDS} from '@github-ui/item-picker-base/constants/ids'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {getAdjustedOverlayProps} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'

import {GlobalCommands} from '@github-ui/ui-commands'
import {TableIcon} from '@primer/octicons-react'
import {SelectPanel, type SelectPanelProps, type SelectPanelItemInput as ItemInput} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import type React from 'react'
import {useCallback, useMemo, useRef, type HTMLAttributes, type RefAttributes} from 'react'

import {ProjectItemsLimitReachedDialog} from './ProjectItemsLimitReachedDialog'

export type ProjectPickerProject = {
  readonly id: string
  readonly title: string
  readonly closed: boolean
  readonly number: number
  readonly url: string
  readonly viewerCanUpdate: boolean
  readonly hasReachedItemsLimit: boolean
}

export type AnchoredOverlayAnchorProps = HTMLAttributes<HTMLElement> & RefAttributes<HTMLButtonElement>

export type ProjectPickerBaseProps = {
  anchorElement: (props: AnchoredOverlayAnchorProps) => React.ReactElement
  title?: string

  filteredProjects: ItemInput[]
  selectedProjects: ItemInput[]
  groups: ItemGroup[]

  onOpenChange: (open: boolean, gesture?: string) => void

  isDialogOpen: boolean
  handleDialogClose: () => void

  insidePortal?: boolean
  onCancel: () => void
  onSelectedChange: (selection: ItemInput[]) => void
} & Pick<SelectPanelProps, 'open' | 'loading' | 'onFilterChange' | 'filterValue'>

export function ProjectPickerBase({
  anchorElement,
  filteredProjects,
  selectedProjects,
  isDialogOpen,
  handleDialogClose,
  loading,
  filterValue,
  open,
  onOpenChange,
  onCancel,
  onFilterChange,
  onSelectedChange,
  insidePortal,
  title,
  groups,
}: ProjectPickerBaseProps) {
  const handleGlobalCommand = useCallback(() => {
    if (open) return

    onOpenChange(true)
  }, [open, onOpenChange])

  const buttonRef = useRef<HTMLButtonElement>(null)
  const regularHeight = filteredProjects.length <= 2 ? 'auto' : 'large'
  const adjustedOverlayProps = getAdjustedOverlayProps(insidePortal, buttonRef, regularHeight)

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  const showNoMatchMessage = useMemo(() => filteredProjects.length === 0, [filteredProjects.length])
  const selectPanelProps = useMemo<SelectPanelProps>(() => {
    const props: SelectPanelProps = {
      renderAnchor: anchorProps => anchorElement(anchorProps),
      anchorRef: buttonRef,
      placeholderText: LABELS.filterProjects,
      items: filteredProjects,
      selected: selectedProjects,
      open,
      onOpenChange,
      onCancel,
      onSelectedChange,
      filterValue,
      onFilterChange,
      showItemDividers: true,
      overlayProps: {
        width: 'medium',
        ...adjustedOverlayProps,
      } as const,
      loading,
      title: title ?? LABELS.selectProjects,
      message: showNoMatchMessage
        ? {
            title: 'No projects were found',
            variant: 'empty',
            body: 'Please try a different search query.',
          }
        : undefined,
    }

    if (groups?.length > 0 && filteredProjects.length > 0) {
      props.groupMetadata = groups
    }

    return props
  }, [
    adjustedOverlayProps,
    anchorElement,
    filterValue,
    filteredProjects,
    groups,
    loading,
    onCancel,
    onFilterChange,
    onOpenChange,
    onSelectedChange,
    open,
    selectedProjects,
    showNoMatchMessage,
    title,
  ])

  return (
    <>
      <GlobalCommands commands={{'item-pickers:open-projects': handleGlobalCommand}} />
      <FeatureFlags flags={{primer_react_select_panel_fullscreen_on_narrow: responsiveOnNarrowEnabled}}>
        <SelectPanel
          aria-label="Project results"
          data-id={IDS.itemPickerRootId}
          data-testid={IDS.itemPickerTestId}
          {...selectPanelProps}
        />
      </FeatureFlags>
      {isDialogOpen && <ProjectItemsLimitReachedDialog onClose={handleDialogClose} returnFocusRef={buttonRef} />}
    </>
  )
}

export type DefaultProjectPickerAnchorProps = {
  firstSelectedProjectTitle?: string
  readonly?: boolean
  /**
   * Whether to render the project picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  anchorProps?: React.HTMLAttributes<HTMLElement> | undefined
}

export function DefaultProjectPickerAnchor({
  firstSelectedProjectTitle,
  readonly,
  nested,
  anchorProps,
}: DefaultProjectPickerAnchorProps) {
  return (
    <SharedPicker
      anchorText={LABELS.noProjects}
      sharedPickerMainValue={firstSelectedProjectTitle}
      anchorProps={readonly ? undefined : anchorProps}
      ariaLabel={LABELS.selectProjects}
      readonly={readonly}
      nested={nested}
      leadingIcon={TableIcon}
      hotKey={undefined}
    />
  )
}

try{ ProjectPickerBase.displayName ||= 'ProjectPickerBase' } catch {}
try{ DefaultProjectPickerAnchor.displayName ||= 'DefaultProjectPickerAnchor' } catch {}